import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useExchange } from '../contexts/ExchangeContext'
import { wizardApi, type ExchangeHubStatus } from '../services/api/wizard'

const USE_DB = import.meta.env.VITE_USE_DB_WIZARD !== 'false'

function getLocalStorageKey(exchangeId: string): string {
  return `hub_status_${exchangeId}`
}

/**
 * Reads localStorage submission flags (legacy format) into ExchangeHubStatus shape.
 * Used as fallback when DB is unavailable or during migration.
 */
function readLegacyStatus(): ExchangeHubStatus {
  const hubSubmissions: ExchangeHubStatus['hubSubmissions'] = {}
  const documentSignatures: ExchangeHubStatus['documentSignatures'] = {}

  const hubKeys = ['sales', 'escrow', 'identification', 'purchase', 'disbursement', 'acquisition', 'irs'] as const
  for (const hub of hubKeys) {
    const submitted = localStorage.getItem(`${hub}_hub_submitted`) === 'true'
    const date = localStorage.getItem(`${hub}_hub_submitted_date`) || undefined
    if (submitted) {
      hubSubmissions[hub] = { submitted: true, date }
    }
  }

  const docKeys = [
    { key: 'exchange_agreement', name: 'exchange_agreement' },
    { key: 'w9', name: 'w9' },
  ]
  for (const doc of docKeys) {
    const signed = localStorage.getItem(`${doc.key}_signed`) === 'true'
    const date = localStorage.getItem(`${doc.key}_signed_date`) || undefined
    const by = localStorage.getItem(`${doc.key}_signed_by`) || undefined
    if (signed) {
      documentSignatures[doc.name] = { signed: true, date, by }
    }
  }

  const identityVerified = localStorage.getItem('demo_identity_verified') === 'true'

  return {
    hubSubmissions,
    documentSignatures,
    escrowData: null,
    calculatorResults: null,
    identityVerified,
  }
}

/**
 * Hook to load/update exchange-level hub status (submissions, signatures, escrow, calculator).
 * DB-first with localStorage as cache/fallback.
 */
export function useExchangeHubStatus() {
  const { user } = useAuth()
  const { currentExchange } = useExchange()
  const exchangeId = currentExchange?.id || user?.id || 'anon'

  const [status, setStatus] = useState<ExchangeHubStatus>(() => {
    // Instant render from localStorage cache
    const cacheKey = getLocalStorageKey(exchangeId)
    const cached = localStorage.getItem(cacheKey)
    if (cached) {
      try {
        return JSON.parse(cached)
      } catch {}
    }
    // Fallback: read legacy localStorage flags
    return readLegacyStatus()
  })
  const [isLoading, setIsLoading] = useState(true)
  const [isSynced, setIsSynced] = useState(false)

  // Load from API in background
  useEffect(() => {
    if (!USE_DB || exchangeId === 'anon') {
      setIsLoading(false)
      return
    }

    let cancelled = false
    const loadFromDb = async () => {
      try {
        const dbStatus = await wizardApi.getHubStatus(exchangeId)
        if (!cancelled) {
          const legacy = readLegacyStatus()
          const merged = {
            hubSubmissions: { ...dbStatus.hubSubmissions, ...legacy.hubSubmissions },
            documentSignatures: { ...dbStatus.documentSignatures, ...legacy.documentSignatures },
            identityVerified: dbStatus.identityVerified || legacy.identityVerified,
            escrowData: dbStatus.escrowData ?? null,
            calculatorResults: dbStatus.calculatorResults ?? null,
          }
          setStatus(merged)
          setIsSynced(true)
          localStorage.setItem(getLocalStorageKey(exchangeId), JSON.stringify(merged))
        }
      } catch (err) {
        console.warn('Failed to load hub status from API, using localStorage:', err)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    loadFromDb()
    return () => { cancelled = true }
  }, [exchangeId])

  // Listen for hub completion events to refresh
  useEffect(() => {
    const refresh = async () => {
      if (USE_DB && exchangeId !== 'anon') {
        try {
          const fresh = await wizardApi.getHubStatus(exchangeId)
          const legacy = readLegacyStatus()
          const merged = {
            hubSubmissions: { ...fresh.hubSubmissions, ...legacy.hubSubmissions },
            documentSignatures: { ...fresh.documentSignatures, ...legacy.documentSignatures },
            identityVerified: fresh.identityVerified || legacy.identityVerified,
            escrowData: fresh.escrowData ?? null,
            calculatorResults: fresh.calculatorResults ?? null,
          }
          setStatus(merged)
          setIsSynced(true)
          localStorage.setItem(getLocalStorageKey(exchangeId), JSON.stringify(merged))
          return
        } catch {
          // fall through to legacy read
        }
      }
      const legacy = readLegacyStatus()
      setStatus(prev => {
        const merged = {
          ...prev,
          hubSubmissions: { ...prev.hubSubmissions, ...legacy.hubSubmissions },
          documentSignatures: { ...prev.documentSignatures, ...legacy.documentSignatures },
          identityVerified: prev.identityVerified || legacy.identityVerified,
        }
        localStorage.setItem(getLocalStorageKey(exchangeId), JSON.stringify(merged))
        return merged
      })
    }

    const events = ['salesHubCompleted', 'identificationHubCompleted', 'purchaseHubCompleted', 'disbursementHubCompleted', 'wizardDataUpdated']
    events.forEach(e => window.addEventListener(e, refresh))
    return () => events.forEach(e => window.removeEventListener(e, refresh))
  }, [exchangeId])

  const updateStatus = useCallback(async (updates: Partial<ExchangeHubStatus>) => {
    // Optimistic update
    setStatus(prev => {
      const merged = {
        ...prev,
        ...(updates.identityVerified !== undefined ? { identityVerified: updates.identityVerified } : {}),
        ...(updates.escrowData !== undefined ? { escrowData: updates.escrowData } : {}),
        ...(updates.calculatorResults !== undefined ? { calculatorResults: updates.calculatorResults } : {}),
        hubSubmissions: { ...prev.hubSubmissions, ...(updates.hubSubmissions || {}) },
        documentSignatures: { ...prev.documentSignatures, ...(updates.documentSignatures || {}) },
      }
      // Cache
      localStorage.setItem(getLocalStorageKey(exchangeId), JSON.stringify(merged))
      return merged
    })

    // Write to DB
    if (USE_DB && exchangeId !== 'anon') {
      try {
        await wizardApi.updateHubStatus(exchangeId, updates)
        setIsSynced(true)
      } catch (err) {
        console.error('Failed to update hub status in DB:', err)
        setIsSynced(false)
      }
    }
  }, [exchangeId])

  /**
   * Check if a specific hub has been submitted.
   */
  const isHubSubmitted = useCallback((hub: string): boolean => {
    if (status.hubSubmissions[hub]?.submitted === true) return true
    if (typeof window !== 'undefined' && localStorage.getItem(`${hub}_hub_submitted`) === 'true') return true
    return false
  }, [status.hubSubmissions])

  /**
   * Check if a specific document has been signed.
   */
  const isDocSigned = useCallback((docName: string): boolean => {
    return status.documentSignatures[docName]?.signed === true
  }, [status.documentSignatures])

  return {
    status,
    isLoading,
    isSynced,
    updateStatus,
    isHubSubmitted,
    isDocSigned,
    exchangeId,
  }
}
